import React, { useState, useEffect } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Form, Input, Modal, Button } from 'antd';
import { FormProps } from 'antd/lib/form';
import { useForm } from 'antd/lib/form/Form';
import { Store } from 'antd/lib/form/interface';
import {
  NetworkDetailsFragment,
  SearchNetworksDocument,
  ListNetworksDocument,
  useUpdateNetworkMutation,
  useCreateNetworkMutation,
} from '../../graphql/generated';
import { getOperationAST } from 'graphql';

import PublicImageUpload from '../utils/PublicImageUpload';
import ColorInput from '../utils/ColorInput';
import ColorThief from 'colorthief';
import { PracticeSearchSelect } from '../autocompletes';

interface NetworkFormProps extends FormProps {
  initialNetwork?: NetworkDetailsFragment;
  updateMutation?: ReturnType<typeof useUpdateNetworkMutation>[0];
  createMutation?: ReturnType<typeof useCreateNetworkMutation>[0];
  showSubmit?: boolean;
  haveValuesChanged?: boolean;
  setHaveValuesChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

const NetworkForm: React.FC<NetworkFormProps> = ({
  form,
  initialNetwork,
  updateMutation,
  createMutation,
  showSubmit,
  haveValuesChanged,
  setHaveValuesChanged,
  ...rest
}) => {
  const [dominantColors, setDominantColors] = useState<string[]>([]);

  const [formInternal] = useForm(form);
  const [
    updateMutationInternal,
    updateMutationInternalResults,
  ] = useUpdateNetworkMutation();
  const [
    createMutationInternal,
    createMutationInternalResults,
  ] = useCreateNetworkMutation();

  // If an initial network is given, this populates the form
  const recordToFormVals = (record: NetworkDetailsFragment) => {
    return {
      ...record,
      default_practice_id: record.default_practice_id
        ? {
            value: record.default_practice?.practice_id,
            label: record.default_practice?.practice_name,
          }
        : undefined,
      network_logo: { url: record.network_logo },
    };
  };
  useEffect(() => {
    if (!initialNetwork) {
      formInternal.setFieldsValue({ network_id: uuidv4() });
    } else {
      formInternal.resetFields();
      formInternal.setFieldsValue(recordToFormVals(initialNetwork));
      if (initialNetwork.network_logo) {
        const image = new Image();
        image.crossOrigin = 'Anonymous';
        image.onload = function () {
          const colorThief = new ColorThief();
          const palette = colorThief.getPalette(image, 8);
          if (palette) {
            const hexcolors = palette.map((triple: any) =>
              rgbToHex(triple[0], triple[1], triple[2])
            );
            setDominantColors(hexcolors);
          }
        };
        image.src = initialNetwork.network_logo;
      }
    }
  }, [formInternal, initialNetwork]);

  // Takes the form vals and puts them into right format for writing
  const formValsToRecordChanges = (formValues: Store) => {
    const { provider_employments, ...rest } = formValues;
    const new_obj = {
      ...rest,
      network_logo: formValues.network_logo?.url || null,
      default_practice_id: formValues.default_practice_id?.value || null,
    };
    const changes = initialNetwork
      ? Object.fromEntries(
          Object.entries(new_obj).filter(
            ([k, v]) => (initialNetwork as any)[k] !== v
          )
        )
      : new_obj;
    return changes;
  };

  const valuesHaveChanged = (formValues: Store) => {
    return Object.keys(formValsToRecordChanges(formValues)).length > 0;
  };

  const handleInsert = (values: any) => {
    const network = formValsToRecordChanges(values);
    (createMutation || createMutationInternal)({
      variables: { network: network },
      refetchQueries: [
        // This just gets the name of the query to refresh.
        // By passing it as just a string, we force a refresh of *all*
        // calls made by the query, regardless of variable values
        // This is useful since we're fetching pages individually, and
        // after inserting a new document then any page might have changed
        getOperationAST(SearchNetworksDocument)?.name?.value || '',
        getOperationAST(ListNetworksDocument)?.name?.value || '',
      ],
    });
  };

  const handleUpdate = (values: any) => {
    if (!initialNetwork) {
      return;
    }
    const changes = formValsToRecordChanges(values);
    (updateMutation || updateMutationInternal)({
      variables: {
        network_id: initialNetwork.network_id,
        changes: Object.keys(changes).length ? changes : undefined,
      },
    });
    if (setHaveValuesChanged) setHaveValuesChanged(false);
  };
  const readFilePromise = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        resolve((reader.result as string)?.toString());
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getColorPalette = async (
    file: File,
    colorCount?: number,
    quality?: number
  ) => {
    return new Promise<number[][]>((resolve, reject) => {
      readFilePromise(file).then((fileContents) => {
        const image = new Image();
        const colorThief = new ColorThief();
        image.onload = function () {
          resolve(colorThief.getPalette(image, colorCount, quality));
        };
        image.src = fileContents;
      });
    });
  };
  const getDominantColor = async (file: File, quality?: number) => {
    return new Promise<number[]>((resolve, reject) => {
      readFilePromise(file).then((fileContents) => {
        const image = new Image();
        const colorThief = new ColorThief();
        image.onload = function () {
          resolve(colorThief.getColor(image, quality));
        };
        image.src = fileContents;
      });
    });
  };
  const rgbToHex = (r: number, g: number, b: number) =>
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('');

  return (
    <Form
      {...rest}
      form={formInternal}
      labelCol={rest.labelCol || { span: 6 }}
      wrapperCol={rest.wrapperCol || { span: 18 }}
      onValuesChange={(_, allValues) =>
        setHaveValuesChanged
          ? setHaveValuesChanged(valuesHaveChanged(allValues))
          : null
      }
      onFinish={initialNetwork ? handleUpdate : handleInsert}
    >
      <Form.Item
        label="Network Name"
        name="network_name"
        rules={[
          {
            required: true,
            whitespace: true,
            message: 'Please provide a network name',
          },
        ]}
      >
        <Input placeholder="Name" />
      </Form.Item>
      <Form.Item label="Slug" name="network_slug">
        <Input placeholder="slug" />
      </Form.Item>
      <Form.Item label="Description" name="network_description">
        <Input placeholder="description" />
      </Form.Item>
      <Form.Item label="ACO ID" name="dc_id">
        <Input placeholder="DXXXX" />
      </Form.Item>
      <Form.Item label="Default Practice" name="default_practice_id">
        <PracticeSearchSelect
          allowClear
          showSearch
          onChange={(value) => {
            form?.setFieldsValue({ default_practice_id: value });
          }}
        />
      </Form.Item>
      <Form.Item label="Broker Name" name="broker_name">
        <Input placeholder="name" />
      </Form.Item>
      <Form.Item label="Broker Phone (emails)" name="broker_phone_for_emails">
        <Input placeholder="phone" />
      </Form.Item>
      <Form.Item label="Broker Phone (letters)" name="broker_phone_for_letters">
        <Input placeholder="phone" />
      </Form.Item>
      <Form.Item label="Broker URL (emails)" name="broker_url_for_emails">
        <Input placeholder="www.brokerage.com" />
      </Form.Item>
      <Form.Item label="Broker URL (letters)" name="broker_url_for_letters">
        <Input placeholder="www.brokerage.com" />
      </Form.Item>
      <Form.Item label="Broker Disclaimer" name="broker_disclaimer">
        <Input.TextArea placeholder="Blah blah, terms apply" autoSize />
      </Form.Item>
      <Form.Item name="network_id" hidden={true}></Form.Item>
      <Form.Item
        label="Logo"
        name="network_logo"
        help="Optimal size is 480 x 180px. Must be .jpg or .png file less than 2MB."
        dependencies={['network_id']}
      >
        <PublicImageUpload
          uploadPath={`network/${formInternal.getFieldValue(
            'network_id'
          )}/logos/${new Date().getTime()}/`}
          cropProps={{
            rotate: true,
            aspect: 480 / 180,
            minZoom: 0.5,
            cropperProps: { restrictPosition: false },
          }}
          previewStyle={{ width: 320, height: 120 }}
          maxFileSize={2 * 1024 * 1024} //2 MB
          targetHeight={180}
          targetWidth={480}
          onChange={(uploadVal) => {
            if (uploadVal.file) {
              getColorPalette(uploadVal.file, 8).then((palette) => {
                if (palette) {
                  const hexcolors = palette.map((triple) =>
                    rgbToHex(triple[0], triple[1], triple[2])
                  );
                  setDominantColors(hexcolors);
                }
              });
              getDominantColor(uploadVal.file).then((color) => {
                if (color) {
                  formInternal.setFieldsValue({
                    network_color: rgbToHex(color[0], color[1], color[2]),
                  });
                }
              });
            }
          }}
        />
      </Form.Item>
      <Form.Item label="Color" name="network_color">
        <ColorInput presetColors={dominantColors} />
      </Form.Item>
      {showSubmit ? (
        <Form.Item style={{ marginTop: 12 }} wrapperCol={{ offset: 6 }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={!haveValuesChanged}
            loading={
              createMutationInternalResults.loading ||
              updateMutationInternalResults.loading
            }
          >
            Save
          </Button>
        </Form.Item>
      ) : null}
    </Form>
  );
};

interface NetworkMutationModalProps {
  formName: string;
  initialRecord?: NetworkDetailsFragment;
  visible: boolean;
  onCancel?: (() => void) | undefined;
  afterSubmit?: (() => void) | undefined;
}

const NetworkMutationModal: React.FC<NetworkMutationModalProps> = ({
  initialRecord,
  visible,
  onCancel,
  afterSubmit,
}) => {
  const [form] = useForm();
  const [updateMutation, updateMutationResult] = useUpdateNetworkMutation({
    onCompleted: () => {
      form.resetFields();
      if (afterSubmit) afterSubmit();
    },
  });
  const [createMutation, createMutationResult] = useCreateNetworkMutation({
    onCompleted: () => {
      form.resetFields();
      if (afterSubmit) afterSubmit();
    },
  });

  const [haveValuesChanged, setHaveValuesChanged] = useState(false);

  return (
    <Modal
      title={initialRecord ? 'Update Network' : 'Create Network'}
      okText={initialRecord ? 'Update Network' : 'Create Network'}
      okButtonProps={{ disabled: initialRecord && !haveValuesChanged }}
      visible={visible}
      onCancel={onCancel}
      onOk={(e) => form.submit()}
      confirmLoading={
        updateMutationResult.loading || createMutationResult.loading
      }
      width={660}
    >
      <NetworkForm
        form={form}
        initialNetwork={initialRecord}
        updateMutation={updateMutation}
        createMutation={createMutation}
        haveValuesChanged={haveValuesChanged}
        setHaveValuesChanged={setHaveValuesChanged}
      />
    </Modal>
  );
};

export { NetworkForm, NetworkMutationModal };
